import {useCallback, useMemo} from 'react';

import {ProcedureRunDataEntryMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {makeReactContext} from 'lib/makeReactContext';
import {SelectStepTasks} from 'notebook/worksheets/workspace/WorksheetUtils';
import {
  PMEIsQuery_procedureMethodExecutionInstances as PMEIOrInaccessible,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode as PMEI,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode as MethodTask,
} from 'procedures/worksheets/__generated/PMEIsQuery';
import {selectStepTasks} from 'procedures/worksheets/WorksheetUtils';

export interface ProcedureWorksheetContextValue {
  PMEIs: Array<PMEIOrInaccessible>;
  /** Refetches the worksheet's PMEIs from the server and resolves to the fetched list. */
  refetchPMEIs: () => Promise<Array<PMEIOrInaccessible>>;
  isProcedureFeatureEnabled: boolean;
}

export interface PMEIAndMethodTask {
  task: MethodTask | null;
  pmei: PMEI;
}

export function getProcedureWorksheetContextFallback(): ProcedureWorksheetContextValue {
  return {
    PMEIs: [],
    refetchPMEIs: () => Promise.resolve([]),
    isProcedureFeatureEnabled: false,
  };
}

export const ProcedureWorksheetContext = makeReactContext<ProcedureWorksheetContextValue>(
  getProcedureWorksheetContextFallback,
  'ProcedureWorksheetContext'
);

export const useProcedureWorksheetContext = (): ProcedureWorksheetContextValue => {
  const context = ProcedureWorksheetContext.useContext();
  return context;
};

/** Whether the worksheet's condition replicates share its steps. */
export const useIsParallelExecutionWorksheet = (): boolean => {
  const {PMEIs} = useProcedureWorksheetContext();
  return PMEIs.some(
    (pmei) =>
      !isInaccessible(pmei) &&
      pmei.procedureRun.defaultDataEntryMode === ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER
  );
};

/** Returns a function that selects a worksheet step's tasks for its status and transitions. */
export const useSelectStepTasks = (): SelectStepTasks => {
  const isParallelExecution = useIsParallelExecutionWorksheet();
  return useCallback((tasks) => selectStepTasks(tasks, isParallelExecution), [isParallelExecution]);
};

/**
 * Pairs each accessible PMEI of a live (non-archived) condition replicate with its method task. Method
 * tasks are linked asynchronously after PMEI creation, so `task` is null until the link exists.
 *
 * Use this when you need method task data (status, transitions, timestamps). Do NOT use this
 * to check PMEI-level properties like procedureRun.defaultDataEntryMode — use
 * useProcedureWorksheetContext().PMEIs instead, which includes PMEIs of archived condition replicates.
 */
export function selectPMEIsAndMethodTasks(PMEIs: Array<PMEIOrInaccessible>): Array<PMEIAndMethodTask> {
  return PMEIs.filter((pmei) => !isInaccessible(pmei))
    .filter((pmei) => !pmei.archiveData.archived)
    .map((pmei) => ({
      task: pmei.methodTask && !isInaccessible(pmei.methodTask) ? pmei.methodTask : null,
      pmei,
    }));
}

/**
 * {@link selectPMEIsAndMethodTasks} applied to the worksheet's loaded PMEIs, and a function that refetches the
 * worksheet's PMEIs from the server and resolves to the same selection of them.
 */
export const usePMEIsAndMethodTasks = (): {
  pmeisAndTasks: Array<PMEIAndMethodTask>;
  refetchPMEIsAndTasks: () => Promise<Array<PMEIAndMethodTask>>;
} => {
  const {PMEIs, refetchPMEIs} = useProcedureWorksheetContext();
  const pmeisAndTasks = useMemo(() => selectPMEIsAndMethodTasks(PMEIs), [PMEIs]);
  const refetchPMEIsAndTasks = useCallback(
    async () => selectPMEIsAndMethodTasks(await refetchPMEIs()),
    [refetchPMEIs]
  );
  return {pmeisAndTasks, refetchPMEIsAndTasks};
};
